import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { CatalogTable } from '@/components/catalog/CatalogTable';
import { CatalogDialog } from '@/components/catalog/CatalogDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { usePolledData } from '@/lib/hooks/usePolledData';
import type { Catalog, CreateCatalogRequest, UpdateCatalogRequest } from '@/types/catalog';

export function CatalogManagementPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  // `deleting` keeps the target through the dialog's exit animation; `deleteOpen` controls visibility.
  const [deleting, setDeleting] = useState<Catalog | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const catalogs = usePolledData(() => api.catalog.list({ show_deleted: showDeleted }), undefined, showDeleted);
  const rows = catalogs.data ?? [];

  const runDelete = async () => {
    if (!deleting || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.catalog.delete(deleting.id);
      setDeleteOpen(false);
      toast.success('Catalog deleted');
      catalogs.refetch();
    } catch (err) {
      setDeleteError(err instanceof Error && err.message ? err.message : 'Failed to delete catalog');
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleRestoreCatalog = async (id: number) => {
    try {
      await api.catalog.restore(id);
      toast.success('Catalog restored');
      catalogs.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to restore catalog');
    }
  };

  const handleCatalogSubmit = async (data: CreateCatalogRequest | UpdateCatalogRequest) => {
    await api.catalog.create(data as CreateCatalogRequest);
    toast.success('Catalog created');
    catalogs.refetch();
  };

  const filteredCatalogs = rows.filter((catalog) => {
    const query = searchQuery.toLowerCase();
    return catalog.name.toLowerCase().includes(query) || catalog.description.toLowerCase().includes(query);
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Catalog"
        description="Catalogs and the stacks users can deploy."
        actions={<Button onClick={() => setCatalogDialogOpen(true)}>Create catalog</Button>}
      />

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <Input
              aria-label="Search catalogs"
              placeholder="Search catalogs…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
            />
            <div className="flex items-center gap-2">
              <input
                id="showDeleted"
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="size-4 accent-primary"
              />
              <label htmlFor="showDeleted" className="text-sm">
                Show deleted
              </label>
            </div>
          </div>

          {catalogs.loading ? (
            <p role="status" className="py-8 text-center text-muted-foreground">
              Loading catalogs…
            </p>
          ) : catalogs.data === undefined ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              <span>{catalogs.error ?? 'Failed to load catalogs'}</span>
              <Button size="sm" variant="outline" onClick={catalogs.refetch}>
                Retry
              </Button>
            </div>
          ) : (
            <>
              {catalogs.error ? (
                <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                  Could not refresh catalogs: {catalogs.error}
                </p>
              ) : null}
              <CatalogTable
                catalogs={filteredCatalogs}
                onDelete={(id) => {
                  const target = rows.find((c) => c.id === id);
                  if (!target) return;
                  setDeleteError(null);
                  setDeleting(target);
                  setDeleteOpen(true);
                }}
                onRestore={handleRestoreCatalog}
                onViewDetails={(catalogId) => navigate(`/catalog/${catalogId}`)}
              />
            </>
          )}
        </CardContent>
      </Card>

      <CatalogDialog open={catalogDialogOpen} onOpenChange={setCatalogDialogOpen} catalog={null} onSubmit={handleCatalogSubmit} />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(next) => {
          if (!next && !deleteBusy) {
            setDeleteOpen(false);
            setDeleteError(null);
          }
        }}
        title="Delete catalog?"
        description={
          deleting
            ? `Delete '${deleting.name}'? It will be hidden from users and can be restored from Show deleted.`
            : undefined
        }
        confirmLabel={deleteBusy ? 'Deleting…' : 'Delete'}
        destructive
        loading={deleteBusy}
        error={deleteError}
        onConfirm={() => void runDelete()}
      />
    </div>
  );
}
